import { useState } from "react";
import { importApi } from "../services/importApi";
import { useToast } from "../context/ToastContext";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { Field } from "../components/common/Field";
import { Badge } from "../components/common/Badge";
import { useFlow } from "../hooks/useFlow";
import { FLOWS } from "../services/flows";

/**
 * Bringing your history in from another app (#275).
 *
 * Nobody switches beekeeping apps if it means re-entering three seasons
 * of inspections, so this is the difference between somebody trying the
 * app and actually moving to it.
 *
 * THE SHAPE: choose a file, check the columns we guessed, SEE WHAT WOULD
 * HAPPEN, then import. The preview is not an optional nicety -- an
 * import that creates four hundred wrong hives is a disaster somebody
 * unpicks by hand, and showing the outcome first is far cheaper than
 * building an undo.
 *
 * Sites before hives, because a hive needs somewhere to be.
 */

const STEPS = { PICK: "pick", MAP: "map", PREVIEW: "preview", DONE: "done" };

const FIELD_LABELS = {
  site: {
    name: "Site name",
    address: "Address",
    notes: "Notes",
  },
  hive: {
    site_name: "Which site it's at",
    hive_number: "Hive name or number",
    hive_type: "Hive type",
    installation_date: "Date started",
  },
};

const REQUIRED = { site: ["name"], hive: ["site_name", "hive_number"] };

export function ImportPage() {
  const { notify } = useToast();
  /* #379's most valuable flow. An import is several steps long and the
     person doing it has a file they are trying to get in; somebody who
     gives up part way is somebody whose data never arrived, and that is
     the drop-off worth knowing about above all others. */
  const flow = useFlow(FLOWS.IMPORT);
  const [entity, setEntity] = useState("site");
  const [step, setStep] = useState(STEPS.PICK);
  const [content, setContent] = useState("");
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  async function chooseFile(file) {
    if (!file) return;
    const text = await file.text();
    setContent(text);
    setBusy(true);
    try {
      const r = await importApi.inspect(entity, text);
      setHeaders(r.headers || []);
      setMapping(r.suggested_mapping || {});
      setRowCount(r.row_count || 0);
      setStep(STEPS.MAP);
    } catch (err) {
      notify(err.apiMessage || "Couldn't read that file", "danger");
    } finally {
      setBusy(false);
    }
  }

  const missing = REQUIRED[entity].filter((f) => !mapping[f]);

  async function runPreview() {
    setBusy(true);
    try {
      // dry_run, always, before anything is written.
      const r = await importApi.run(entity, content, mapping, true);
      setPreview(r);
      setStep(STEPS.PREVIEW);
    } catch (err) {
      notify(err.apiMessage || "Couldn't read that file", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    setBusy(true);
    try {
      const r = await importApi.run(entity, content, mapping, false);
      /* The REAL run, not the dry run above it. A preview is a step in
         the flow, not the end of it -- counting it would report every
         abandoned import as a success. */
      flow.finished();
      setPreview(r);
      setStep(STEPS.DONE);
      notify(`Imported ${r.created}. Nothing else was changed.`, "success");
    } catch (err) {
      notify(err.apiMessage || "Couldn't finish that import", "danger");
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setStep(STEPS.PICK);
    setContent(""); setHeaders([]); setMapping({}); setPreview(null);
  }

  return (
    <div>
      <h1>Bring your data in</h1>
      <p style={{ color: "var(--color-text-muted)", maxWidth: "62ch" }}>
        Export a CSV from whatever you're using now and bring your sites and
        hives across. We'll show you exactly what will happen before anything
        is saved — and running the same file twice won't create duplicates.
      </p>

      {step === STEPS.PICK && (
        <Card title="1. Choose what you're importing">
          <Field label="What's in this file?">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={entity}
                onChange={(e) => setEntity(e.target.value)}>
                <option value="site">Sites — your apiary locations</option>
                <option value="hive">Hives</option>
              </select>
            )}
          </Field>
          {entity === "hive" && (
            // Said before the file is chosen, not after the import fails
            // on four hundred rows.
            <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              Import your sites first — a hive needs somewhere to be.
            </p>
          )}
          <Field label="Your CSV file">
            {(id, aria) => (
              <input id={id} {...aria} type="file" accept=".csv,text/csv"
                className="touch-target"
                onChange={(e) => chooseFile(e.target.files?.[0])} />
            )}
          </Field>
          {busy && <p style={{ margin: 0 }}>Reading…</p>}
        </Card>
      )}

      {step === STEPS.MAP && (
        <Card title={`2. Check the columns — ${rowCount} row${rowCount === 1 ? "" : "s"}`}>
          <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
            We've guessed these from your headings. Change anything we got wrong.
          </p>
          {Object.entries(FIELD_LABELS[entity]).map(([field, label]) => (
            <Field key={field} label={label + (REQUIRED[entity].includes(field) ? " (required)" : "")}>
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target"
                  value={mapping[field] || ""}
                  onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value }))}>
                  <option value="">— not in this file —</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              )}
            </Field>
          ))}
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
            <Button onClick={runPreview} disabled={busy || missing.length > 0}>
              {busy ? "Checking…" : "See what will happen"}
            </Button>
            <Button variant="ghost" onClick={startOver}>Start over</Button>
          </div>
          {missing.length > 0 && (
            <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              Still need: {missing.map((f) => FIELD_LABELS[entity][f]).join(", ")}.
            </p>
          )}
        </Card>
      )}

      {(step === STEPS.PREVIEW || step === STEPS.DONE) && preview && (
        <Card title={step === STEPS.DONE ? "Imported" : "3. What will happen"}>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
            <Badge tone="success">{preview.created} new</Badge>
            {preview.skipped > 0 && <Badge tone="neutral">{preview.skipped} already here</Badge>}
            {preview.failed > 0 && <Badge tone="danger">{preview.failed} can&apos;t be read</Badge>}
          </div>

          {preview.failed > 0 && (
            // Line numbers, because the person fixing this is looking at
            // a spreadsheet. And the rest still import -- one typo on
            // line 340 must not cost them the other 399 rows.
            <div style={{ marginBottom: "var(--space-3)" }}>
              <strong>These rows will be skipped:</strong>
              <ul>
                {preview.rows.filter((r) => r.status === "error").slice(0, 20).map((r) => (
                  <li key={r.line}>Line {r.line}: {r.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {step === STEPS.PREVIEW ? (
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <Button onClick={commit} disabled={busy || preview.created === 0}>
                {busy ? "Importing…" : `Import ${preview.created}`}
              </Button>
              <Button variant="ghost" onClick={() => setStep(STEPS.MAP)}>Back</Button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              {/* The next thing they need, not a dead end. */}
              {entity === "site" && (
                <Button onClick={() => { startOver(); setEntity("hive"); }}>
                  Now import your hives
                </Button>
              )}
              <Button variant="secondary" onClick={startOver}>Import another file</Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
